import {
  AUTO_SEND_REFUSED_MESSAGE,
  CODE_ENDED_RESEND_PROMPT,
} from "components/OtpVerification/ended-code";
import { verifyPathFor } from "pages/StartLead/destinations";
import type {
  ConfirmFailureReason,
  ConfirmSurface,
  ConfirmTrigger,
  EventPurpose,
  LinkFailureKind,
} from "pages/StartLead/events";
import type { LeadKind } from "pages/StartLead/flow";
import {
  FIRST_CALL_PHONE_HREF,
  FIRST_CALL_PHONE_LABEL,
  SUPPORT_PHONE_HREF,
  SUPPORT_PHONE_LABEL,
} from "constants/phones";
import { otpSessionFields, type OtpSessions } from "helpers/otp-sessions";
import { safeRedirectPath } from "helpers/redirect";
import { getApiErrorMessage } from "utils/errors";
import { submissionIdFromSearch } from "pages/StartResume/resume";

export type GrantMethod = "sms_otp" | "email_link";
export type GrantPurpose =
  | "lead_confirmation"
  | "account_from_quote"
  | "channel_sign_in"
  | "quote_reentry";

/** The answer to `POST /auth/login-grants/lookup`: what the grant's typed
 * destinations are and which of them may be used. The person never
 * chooses a channel the server did not bind. */
export type GrantLookup = {
  purpose: GrantPurpose;
  methods: GrantMethod[];
  phoneDisplay: string | null;
  emailDisplay: string | null;
  redirectPath: string;
  /** What lets the visitor correct a mistyped destination on the screen: the
   * signed link this browser arrived on, or the ZIP code from the form;
   * null when neither applies. */
  correctionProof: CorrectionProof;
};

export type CorrectionProof = "arrival" | "zip" | null;

/** The proof a response names, or null for anything else, so an answer
 * from a newer server never fails to read. */
export function correctionProofOf(value: unknown): CorrectionProof {
  return value === "arrival" || value === "zip" ? value : null;
}

const PURPOSES: readonly string[] = [
  "lead_confirmation",
  "account_from_quote",
  "channel_sign_in",
  "quote_reentry",
];
const METHODS: readonly string[] = ["sms_otp", "email_link"];

function displayOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/** Reads a lookup response defensively; anything that does not name a
 * known purpose and at least one usable method with its destination is
 * treated as an invalid grant. */
export function parseGrantLookup(raw: unknown): GrantLookup | null {
  if (typeof raw !== "object" || raw === null) return null;
  const body = raw as Record<string, unknown>;
  if (typeof body.purpose !== "string" || !PURPOSES.includes(body.purpose)) {
    return null;
  }
  if (!Array.isArray(body.methods)) return null;

  const phoneDisplay = displayOrNull(body.phone_display);
  const emailDisplay = displayOrNull(body.email_display);
  const methods = body.methods.filter(
    (method): method is GrantMethod =>
      typeof method === "string" && METHODS.includes(method),
  );
  const usable = methods.filter((method) =>
    method === "sms_otp" ? phoneDisplay !== null : emailDisplay !== null,
  );
  if (usable.length === 0) return null;

  return {
    purpose: body.purpose as GrantPurpose,
    methods: usable,
    phoneDisplay,
    emailDisplay,
    redirectPath: safeRedirectPath(
      typeof body.redirect_path === "string" ? body.redirect_path : null,
    ),
    correctionProof: correctionProofOf(body.correction_proof),
  };
}

/** The method the screen leads with: the one the visitor chose, when the
 * grant offers it; else a texted code when allowed; else the emailed link. */
export function primaryMethod(
  lookup: GrantLookup,
  chosen: GrantMethod | null = null,
): GrantMethod {
  if (chosen !== null && lookup.methods.includes(chosen)) return chosen;
  return lookup.methods.includes("sms_otp") ? "sms_otp" : "email_link";
}

/** What a host screen hears of the code or link step, beside the lead
 * flow's own events. */
export interface GrantConfirmReport {
  sent(method: GrantMethod, trigger: ConfirmTrigger): void;
  accepted(method: GrantMethod): void;
  failed(reason: ConfirmFailureReason): void;
}

export type SupportLineKind = "first_call" | "pre_need";

export const SUPPORT_LINES: Record<
  SupportLineKind,
  { href: string; label: string }
> = {
  first_call: { href: FIRST_CALL_PHONE_HREF, label: FIRST_CALL_PHONE_LABEL },
  pre_need: { href: SUPPORT_PHONE_HREF, label: SUPPORT_PHONE_LABEL },
};

/** Which number a family should call from this screen: the pre-need line
 * only for a lead known to be planning ahead, otherwise the first-call line,
 * which answers around the clock and can route any caller. */
export function supportLineKindFor(
  leadKind: LeadKind | undefined,
): SupportLineKind {
  return leadKind === "planning" ? "pre_need" : "first_call";
}

export type GrantFailureKind =
  | "grant_invalid"
  | "wrong_code"
  | "code_expired"
  | "rate_limited"
  | "cannot_sign_in"
  | "send_failed"
  | "text_unavailable"
  | "email_failed"
  | "other";

/** Every grant failure, a lockout included, answers with the one
 * `login_grant_invalid` code so the screen cannot be used to learn which
 * it was. The other codes are the OTP provider's and the sign-in guards'.
 * Wire values are lowercase; the contract names them in upper case, so
 * the comparison ignores case. */
export function grantFailureKind(
  errorCode: string | undefined,
  statusCode: number | undefined,
): GrantFailureKind {
  switch (errorCode?.toLowerCase()) {
    case "login_grant_invalid":
      return "grant_invalid";
    case "token_invalid":
      return "wrong_code";
    case "token_expired":
      return "code_expired";
    case "too_many_attempts":
      return "rate_limited";
    case "phone_mismatch":
    case "user_lookup_failed":
      return "cannot_sign_in";
    case "sms_unavailable":
      return "text_unavailable";
  }
  return statusCode === 429 ? "rate_limited" : "other";
}

/** What a failed grant look-up means: the grant is gone, the server asked to
 * wait, or the answer never came. Only the first says the session ended. */
export type LookupFailure = "grant_invalid" | "rate_limited" | "unreachable";

export function lookupFailureOf(
  errorCode: string | undefined,
  statusCode: number | undefined,
): LookupFailure {
  const kind = grantFailureKind(errorCode, statusCode);
  if (kind === "grant_invalid" || kind === "rate_limited") return kind;
  if (statusCode === undefined || statusCode >= 500) return "unreachable";
  return "grant_invalid";
}

/** What a failed step is: its kind, which picks the copy, and the reason it
 * reports to analytics. */
export type GrantFailure = {
  kind: GrantFailureKind;
  reason: ConfirmFailureReason;
};

const FAILURE_REASON: Record<GrantFailureKind, ConfirmFailureReason> = {
  grant_invalid: "grant_invalid",
  wrong_code: "wrong_code",
  code_expired: "code_expired",
  rate_limited: "rate_limited",
  cannot_sign_in: "cannot_sign_in",
  send_failed: "recaptcha_failed",
  text_unavailable: "sms_unavailable",
  email_failed: "other",
  other: "other",
};

/** The reason a failed step reports: the server's own answer, or `network`
 * when the request never got one. */
export function failureReasonFor(
  kind: GrantFailureKind,
  reachedServer: boolean,
): ConfirmFailureReason {
  return reachedServer ? FAILURE_REASON[kind] : "network";
}

/** A text that could not be requested because no token could be had to ask
 * with. */
export const SEND_NOT_STARTED: GrantFailure = {
  kind: "send_failed",
  reason: FAILURE_REASON.send_failed,
};

export const SESSION_ENDED_TITLE = "That session ended";
export const SESSION_ENDED_BODY = "Let's send you a new one.";

/** What the ended screen says when a replacement cannot be sent to the
 * destination it knows. */
export const SESSION_ENDED_ELSEWHERE_BODY =
  "Use a different email or phone and we'll send you a code.";

/** The one line an embedded screen says when its session ended. */
export const SESSION_ENDED_COPY = `${SESSION_ENDED_TITLE}. ${SESSION_ENDED_BODY}`;

export const UNREACHABLE_TITLE = "We couldn't load this just now.";

/** What the screen says beside the retry when the grant could not be looked
 * up: a wait when the server asked for one, a plain retry otherwise. */
export function unreachableBody(rateLimited: boolean): string {
  return rateLimited
    ? "Please wait a moment, then try again."
    : "Please try again.";
}

/** The way forward a failure's copy points to: the emailed link when the
 * grant offers it, otherwise a call. The copy never carries a number; the
 * screen's call line does. */
/** The way around a failed text that a failure message points to: the emailed
 * link on this screen, the emailed link on the screen before it, or a call. */
export type FailureFallback =
  | { way: "email_link" }
  | { way: "email_link_on_previous_step" }
  | { way: "call" };

export function failureFallbackFor(
  emailLinkAvailable: boolean,
): FailureFallback {
  return emailLinkAvailable ? { way: "email_link" } : { way: "call" };
}

/** The fallback for a screen without its own emailed-link control, where the
 * visitor reaches the link by going back. */
export function previousStepFallbackFor(
  emailLinkAvailable: boolean,
): FailureFallback {
  return emailLinkAvailable
    ? { way: "email_link_on_previous_step" }
    : { way: "call" };
}

type FailureCopy = (fallback: FailureFallback) => string;

function sameCopy(message: string): FailureCopy {
  return () => message;
}

function copyByFallback(
  messages: Record<FailureFallback["way"], string>,
): FailureCopy {
  return (fallback) => messages[fallback.way];
}

/** What a failed code request is worded as: the failures that have their own
 * copy keep it; every other one is a send that did not go. */
const SMS_SEND_FAILURE_KIND: Record<GrantFailureKind, GrantFailureKind> = {
  grant_invalid: "grant_invalid",
  wrong_code: "wrong_code",
  code_expired: "code_expired",
  rate_limited: "rate_limited",
  cannot_sign_in: "cannot_sign_in",
  text_unavailable: "text_unavailable",
  send_failed: "send_failed",
  email_failed: "email_failed",
  other: "send_failed",
};

export function smsSendFailureKind(kind: GrantFailureKind): GrantFailureKind {
  return SMS_SEND_FAILURE_KIND[kind];
}

/** What a failed emailed link is worded as: one copy for every failure but
 * an ended grant, which has its own. */
export function emailSendFailureKind(kind: GrantFailureKind): GrantFailureKind {
  return kind === "grant_invalid" ? kind : "email_failed";
}

export const EMAIL_LINK_FAILED_MESSAGE =
  "We couldn't email you a link just now. Try again, or give us a call.";

const FAILURE_MESSAGE: Record<GrantFailureKind, FailureCopy> = {
  grant_invalid: sameCopy(SESSION_ENDED_COPY),
  wrong_code: sameCopy("That code didn't match. Check it and try again."),
  code_expired: sameCopy(CODE_ENDED_RESEND_PROMPT),
  rate_limited: sameCopy(
    "That didn't go through. Please wait a moment and try again, or call us and we'll help.",
  ),
  cannot_sign_in: sameCopy(
    "We couldn't sign you in with this phone. Please call us.",
  ),
  send_failed: copyByFallback({
    email_link:
      "We couldn't send a code just now. Try again, or we can email you a link instead.",
    email_link_on_previous_step:
      "We couldn't send a code just now. Try again, or go back to get a sign-in link by email.",
    call: "We couldn't send a code just now. Try again, or give us a call.",
  }),
  email_failed: sameCopy(EMAIL_LINK_FAILED_MESSAGE),
  text_unavailable: copyByFallback({
    email_link:
      "We can't text a code to this phone right now. We can email you a link instead.",
    email_link_on_previous_step:
      "We can't text a code to this phone right now. Go back to get a sign-in link by email.",
    call: "We can't text a code to this phone right now. Please give us a call.",
  }),
  other: sameCopy("Something went wrong. Please try again."),
};

export function grantFailureMessage(
  kind: GrantFailureKind,
  fallback: FailureFallback,
): string {
  return FAILURE_MESSAGE[kind](fallback);
}

/** Failures of asking for a code, worded by this module even where the
 * screen is not a grant's (the sign-in page, an invite). */
const SEND_FAILURE_KINDS: readonly GrantFailureKind[] = [
  "send_failed",
  "text_unavailable",
];

/** The copy for a failed code request outside the grant screen: this
 * module's own for the failures it words, else the API code's message. */
export function smsRequestFailureMessage(
  errorCode: string | undefined,
  statusCode: number | undefined,
  fallback: FailureFallback,
): string {
  const kind = grantFailureKind(errorCode, statusCode);
  if (SEND_FAILURE_KINDS.includes(kind)) {
    return grantFailureMessage(kind, fallback);
  }
  // No code means the send never got an answer it could explain (a dropped
  // connection, a 5xx): it is a code that did not go out. An uncoded 429
  // still asks the visitor to wait.
  if (errorCode === undefined) {
    const uncoded = kind === "rate_limited" ? kind : "send_failed";
    return grantFailureMessage(uncoded, fallback);
  }
  return getApiErrorMessage(errorCode);
}

/** The copy for a failed code request the screen made itself after a code
 * ended: the provider's refusal has its own words, every other failure reads
 * as it does for the visitor's Resend. */
export function endedCodeSendFailureMessage(
  errorCode: string | undefined,
  statusCode: number | undefined,
  fallback: FailureFallback,
): string {
  if (grantFailureKind(errorCode, statusCode) === "rate_limited") {
    return AUTO_SEND_REFUSED_MESSAGE;
  }
  return smsRequestFailureMessage(errorCode, statusCode, fallback);
}

/** Failures of checking a typed code, worded by this module even where the
 * screen is not a grant's. The API code's messages for these speak of an
 * emailed link. */
const CODE_CHECK_FAILURE_KINDS: readonly GrantFailureKind[] = [
  "wrong_code",
  "code_expired",
];

/** The copy for a typed code `POST /auth/verify-sms-otp` refused outside the
 * grant screen: this module's own for a wrong or expired code, else the API
 * code's message. */
export function smsVerifyFailureMessage(
  errorCode: string | undefined,
  statusCode: number | undefined,
  fallback: FailureFallback,
): string {
  const kind = grantFailureKind(errorCode, statusCode);
  if (CODE_CHECK_FAILURE_KINDS.includes(kind)) {
    return grantFailureMessage(kind, fallback);
  }
  return getApiErrorMessage(errorCode);
}

/** The body of `POST /auth/verify-sms-otp` for a grant: the code is checked
 * against the latest text's session, and the one before it when there is
 * one. */
export function verifySmsOtpBody(
  loginGrant: string,
  sessions: OtpSessions,
  otpCode: string,
) {
  return { login_grant: loginGrant, ...otpSessionFields(sessions), otpCode };
}

export const CHANGE_DESTINATION_LABEL = "Use a different email or phone";

/** The sign-in screen's "already signed in, go on" shortcut. In grant mode
 * a session is not proof for the submission being verified, so the
 * visitor stays on the screen and verifies through the grant. */
export function shouldRedirectAfterLogin({
  loginGrant,
  user,
}: {
  loginGrant: string | null;
  user: unknown;
}): boolean {
  return Boolean(user) && loginGrant === null;
}

/** The label on the primary send button: it names what is being sent,
 * whichever button started the send. */
export function sendButtonLabel(
  idleLabel: string,
  sending: GrantMethod | null,
): string {
  if (sending === null) return idleLabel;
  return sending === "email_link" ? "Sending link..." : "Sending code...";
}

/** The label on a button that sends `own`: it says what is being sent only
 * while its own send is under way, so another button's send never gives two
 * controls the same words. */
export function ownSendLabel(
  idleLabel: string,
  own: GrantMethod,
  sending: GrantMethod | null,
): string {
  if (sending !== own) return idleLabel;
  return sendButtonLabel(idleLabel, sending);
}

export type RecaptchaStatus = "loading" | "ready" | "failed";

/** What the confirm screen sends on arrival: the texted code when it is
 * offered and reCAPTCHA is ready (`wait` until it is known), the emailed
 * link when the code is not offered or cannot be requested, and nothing
 * when neither can be sent. */
export type AutoSend = "wait" | "none" | GrantMethod;

export function autoSendPlan(
  lookup: GrantLookup,
  recaptcha: RecaptchaStatus,
  chosen: GrantMethod | null = null,
): AutoSend {
  if (primaryMethod(lookup, chosen) === "email_link") return "email_link";
  if (recaptcha === "ready") return "sms_otp";
  if (recaptcha === "loading") return "wait";
  return lookup.methods.includes("email_link") ? "email_link" : "none";
}

/** Records that a grant's automatic send is under way. True for the one
 * caller that should send; false for a repeat (a remounted effect or
 * screen), which must not send again. */
export function claimAutoSend(sent: Set<string>, grant: string): boolean {
  if (sent.has(grant)) return false;
  sent.add(grant);
  return true;
}

/** Why an emailed link no longer opens, as the server's code says it:
 * past its time, already used, or not one it holds. Null for any other
 * failure. */
export type DeadLinkKind = Extract<
  LinkFailureKind,
  "expired" | "used" | "invalid"
>;

export function deadLinkKindOf(
  errorCode: string | undefined,
): DeadLinkKind | null {
  const code = errorCode?.toLowerCase();
  if (code === undefined) return null;
  if (code === "token_expired") return "expired";
  if (code === "token_invalid") return "used";
  if (code === "login_grant_invalid" || code.startsWith("token_")) {
    return "invalid";
  }
  return null;
}

/** What a failed link reports: why it is dead when the code says, else
 * `other`. */
export function linkFailureKindOf(
  errorCode: string | undefined,
): LinkFailureKind {
  return deadLinkKindOf(errorCode) ?? "other";
}

/** The dead-link screen a failed link shows, when it is one of them. A link
 * with no token is as dead as one the server does not hold. */
export function deadLinkShownFor(kind: LinkFailureKind): DeadLinkKind | null {
  if (kind === "missing") return "invalid";
  return kind === "expired" || kind === "used" || kind === "invalid"
    ? kind
    : null;
}

export const DEAD_LINK_TITLE = "This link no longer works";

const DEAD_LINK_REASON: Record<DeadLinkKind, string | null> = {
  expired: "This link has expired.",
  used: "This link has already been used.",
  invalid: null,
};

/** The short line under a dead link's title, when the code says why. */
export function deadLinkReasonOf(kind: DeadLinkKind): string | null {
  return DEAD_LINK_REASON[kind];
}

const SUBMISSION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const VERIFY_OR_RESUME =
  /^\/start\/(?:verify|resume)\?s=([^&\s]+)(?:&via=reminder)?$/;

/** The purpose a link or grant leads to, read from the path it opens. Null
 * when the path is not one a known purpose leads to. */
export function purposeOfRedirect(path: string | null): GrantPurpose | null {
  if (path === null) return null;
  if (VERIFY_OR_RESUME.test(path)) return "lead_confirmation";
  if (path === "/start") return "channel_sign_in";
  if (QUOTE_ACCOUNT_PATH.test(path)) return "account_from_quote";
  if (isQuotePath(path)) return "quote_reentry";
  return null;
}

/** The purpose to report for a link or grant, `unknown` when nothing names
 * one. */
export function eventPurposeOf(path: string | null): EventPurpose {
  return purposeOfRedirect(path) ?? "unknown";
}

/** The screen a link opened from, as far as its purpose and path say. */
export function linkSurfaceOf(path: string | null): ConfirmSurface {
  switch (purposeOfRedirect(path)) {
    case "lead_confirmation":
      return "verify";
    case "account_from_quote":
      return "account";
    case "quote_reentry":
      return "reentry";
    case "channel_sign_in":
      return "start_panel";
    case null:
      return "login";
  }
}

/** Where a dead link's one action goes, always inside the app: the verify
 * page for a submission's link (which recovers on its own), the quote page
 * for a quote's, and the first screen for anything else. */
export function deadLinkRecoveryPath(redirect: string | null): string {
  const path = safeRedirectPath(redirect, "/start");
  const submission = VERIFY_OR_RESUME.exec(path)?.[1];
  if (submission !== undefined) {
    return SUBMISSION_ID.test(submission)
      ? verifyPathFor(submission.toLowerCase())
      : "/start";
  }
  if (isQuotePath(path) || QUOTE_ACCOUNT_PATH.test(path)) return path;
  return "/start";
}

/** What a lapsed grant can be replaced for: a lead submission still awaiting
 * confirmation, with the methods a replacement can be sent by when they are
 * known, or the quote an account is being made from. */
export type RenewalTarget =
  | { kind: "submission"; submissionId: string; methods: GrantMethod[] | null }
  | { kind: "account"; quoteId: string };

/** What a lapsed grant was minted for, when it can be named: the quote of an
 * account upgrade, else the lead-confirmation grant's own redirect path,
 * else the result stored on this device that holds this very grant, else
 * the submission the page's own `?s=` names (a reload, or an arrival with no
 * stored result). Null when nothing names one. */
export function renewalTargetFor({
  grant,
  lookup,
  stored,
  quoteId = null,
  searchSubmissionId = null,
}: {
  grant: string;
  lookup: GrantLookup | null;
  stored: {
    submissionId: string | null;
    verification: { grant: string; methods: GrantMethod[] } | null;
  } | null;
  quoteId?: string | null;
  searchSubmissionId?: string | null;
}): RenewalTarget | null {
  if (
    quoteId !== null &&
    (lookup === null || lookup.purpose === "account_from_quote")
  ) {
    return { kind: "account", quoteId };
  }
  if (lookup !== null) {
    if (lookup.purpose !== "lead_confirmation") return null;
    const query = lookup.redirectPath.split("?")[1] ?? "";
    const id = lookup.redirectPath.startsWith("/start/resume?")
      ? submissionIdFromSearch(`?${query}`)
      : null;
    if (id !== null) {
      return { kind: "submission", submissionId: id, methods: lookup.methods };
    }
  }
  if (stored?.submissionId != null && stored.verification?.grant === grant) {
    return {
      kind: "submission",
      submissionId: stored.submissionId,
      methods: stored.verification.methods,
    };
  }
  if (searchSubmissionId !== null) {
    return {
      kind: "submission",
      submissionId: searchSubmissionId,
      methods: lookup?.methods ?? null,
    };
  }
  return null;
}

/** The button that asks for a replacement, named for what it sends when the
 * methods are known. */
export function renewalButtonLabel(target: RenewalTarget): string {
  if (target.kind === "submission" && target.methods !== null) {
    if (target.methods.includes("sms_otp")) return "Text me a new code";
    if (target.methods.length > 0) return "Email me a new link";
  }
  return "Send a new code";
}

/** The method a retry of the replacement stands for. */
export function renewalMethodOf(target: RenewalTarget): GrantMethod {
  return target.kind === "submission" &&
    target.methods !== null &&
    target.methods.length > 0 &&
    !target.methods.includes("sms_otp")
    ? "email_link"
    : "sms_otp";
}

/** The purpose a renewal target reports. */
export function renewalPurposeOf(target: RenewalTarget | null): EventPurpose {
  if (target === null) return "unknown";
  return target.kind === "account" ? "account_from_quote" : "lead_confirmation";
}

/** The router state for the history entry after a renewal: the same state
 * with the replacement grant, so a reload or a return to the entry
 * resumes on the grant that works. */
export function renewedHistoryState(
  state: unknown,
  grant: string,
): Record<string, unknown> {
  const base =
    typeof state === "object" && state !== null
      ? (state as Record<string, unknown>)
      : {};
  return { ...base, loginGrant: grant };
}

/** Where a proven grant came from, as its verify answer states it. Null
 * when the answer leaves a field out. */
export type GrantOrigin = {
  purpose: GrantPurpose | null;
  redirectPath: string | null;
};

/** What a proven grant leads to. A code or link for a known account signs
 * in; for a channel no Person holds, it hands over a single-use token to
 * create the account with; for a channel on an account that signs in
 * another way, it only says so. A grant made to open one quote reports a
 * channel that reaches nobody as `not_found`, never as an account to
 * create. */
export type GrantCompletion =
  | ({ kind: "signed_in"; customToken: string } & GrantOrigin)
  | ({ kind: "create_account"; signupToken: string } & GrantOrigin)
  | ({ kind: "use_account_channel" } & GrantOrigin)
  | ({ kind: "not_found" } & GrantOrigin)
  | { kind: "failed" };

/** The id of a quote: a UUID, or a legacy Firestore id. */
export const QUOTE_ID_PATTERN = "[A-Za-z0-9_-]{1,64}";

const QUOTE_PATH = new RegExp(`^/quotes/${QUOTE_ID_PATTERN}$`);
const QUOTE_ACCOUNT_PATH = new RegExp(`^/quotes/${QUOTE_ID_PATTERN}/account$`);

/** Whether a path is the page of one quote. */
export function isQuotePath(path: string): boolean {
  return QUOTE_PATH.test(path);
}

/** The purpose a verified link was for. The answer names it; when it does
 * not (an older server), it is read from the one thing the answer carries
 * about its grant: the server-fixed path it leads to. A channel sign-in
 * bound to a quote's page is that quote's re-entry. Null when the answer
 * is not a grant's (an ordinary sign-in link, which has no path) or the
 * path is not one a known purpose leads to. */
export function purposeOfCompletion(
  completion: GrantCompletion,
): GrantPurpose | null {
  if (completion.kind === "failed") return null;
  const { purpose, redirectPath } = completion;
  if (purpose !== null) {
    return purpose === "channel_sign_in" &&
      redirectPath !== null &&
      isQuotePath(redirectPath)
      ? "quote_reentry"
      : purpose;
  }
  switch (completion.kind) {
    case "create_account":
    case "use_account_channel":
      return "channel_sign_in";
    case "not_found":
      return "quote_reentry";
    case "signed_in": {
      if (redirectPath === null) return null;
      if (redirectPath === "/start") return "channel_sign_in";
      if (isQuotePath(redirectPath)) return "quote_reentry";
      if (redirectPath.startsWith("/start/resume")) return "lead_confirmation";
      return null;
    }
  }
}

/** The quote page a proof that opened no account sends the visitor back
 * to: a link made to open one quote that reached nobody who holds the
 * quote, or whose channel nobody holds, reads as not found there. Null for
 * any other proof, or when the answer does not name the quote's page. */
export function quoteNotFoundPathOf(
  completion: GrantCompletion,
): string | null {
  if (completion.kind !== "not_found" && completion.kind !== "create_account") {
    return null;
  }
  if (purposeOfCompletion(completion) !== "quote_reentry") return null;
  const path = completion.redirectPath;
  return path !== null && isQuotePath(path) ? path : null;
}

function grantOriginOf(body: Record<string, unknown>): GrantOrigin {
  return {
    purpose:
      typeof body.purpose === "string" && PURPOSES.includes(body.purpose)
        ? (body.purpose as GrantPurpose)
        : null,
    redirectPath:
      typeof body.redirect_path === "string" ? body.redirect_path : null,
  };
}

/** Reads the answer to a verified code or link defensively: anything that
 * is not a success naming one of the outcomes above is `failed`. */
export function parseGrantCompletion(raw: unknown): GrantCompletion {
  if (typeof raw !== "object" || raw === null) return { kind: "failed" };
  const body = raw as Record<string, unknown>;
  if (body.success !== true) return { kind: "failed" };
  const origin = grantOriginOf(body);
  if (typeof body.custom_token === "string" && body.custom_token !== "") {
    return { kind: "signed_in", customToken: body.custom_token, ...origin };
  }
  if (
    body.next === "create_account" &&
    typeof body.signup_token === "string" &&
    body.signup_token !== ""
  ) {
    return {
      kind: "create_account",
      signupToken: body.signup_token,
      ...origin,
    };
  }
  if (body.next === "use_account_channel") {
    return { kind: "use_account_channel", ...origin };
  }
  if (body.next === "not_found") return { kind: "not_found", ...origin };
  return { kind: "failed" };
}
